import { useEffect, useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import QRCode from 'qrcode'
import { Copy, Download, ShieldCheck, ShieldOff, Trash2 } from 'lucide-react'
import { authApi, describeError, type DataExport, type PhoneStart } from '@/lib/auth'
import { useVerifyStatus } from '@/lib/useVerifyStatus'
import { formatSecret, generateTotpSecret, otpauthUrl } from '@/lib/totp'
import { formatDateTime } from '@/lib/format'
import { useAuthStore } from '@/store/auth'
import { cx } from '@/lib/cx'
import { CodeInput, Field } from '@/pages/AuthPage'
import { CodeFallback } from '../CodeFallback'
import { Modal } from './Modal'
import { DevicesSection } from './DevicesSection'
import { UsernameModal } from './UsernameModal'
import layer from './SettingsLayer.module.css'
import auth from '@/pages/AuthPage.module.css'
import styles from './AccountSection.module.css'

type Dialog = 'username' | 'phone' | 'email' | 'name' | 'password' | 'totp-enable' | 'totp-disable' | 'codes' | 'delete' | null

export function AccountSection() {
  const user = useAuthStore((s) => s.user)
  const mfa = useQuery({ queryKey: ['mfa'], queryFn: authApi.mfaStatus })
  const [dialog, setDialog] = useState<Dialog>(null)
  const setUser = useAuthStore((s) => s.setUser)
  const [resend, setResend] = useState<'idle' | 'busy' | 'sent' | 'error'>('idle')
  if (!user) return null
  const resendEmail = async () => {
    setResend('busy')
    try {
      setUser(await authApi.emailResend())
      setResend('sent')
    } catch {
      setResend('error')
    }
  }
  const enabled = mfa.data?.totp_enabled ?? user.mfa_enabled

  return (
    <section>
      <h1 className={layer.sectionTitle}>Учётная запись</h1>
      {user.deletion_requested_at ? <DeletionNotice /> : null}
      <h2 className={layer.subTitle}>Информация об учётной записи</h2>
      {/* имя, аватар, баннер и «о себе» — в «Профиле»: он открывается карточкой над разделами */}
      <div className={styles.fields}>
        <Row label="Имя пользователя" value={`@${user.username}`} action="Изменить" onAction={() => setDialog('username')} />
        <Row label="Номер телефона" value={user.phone || 'Не указан'} action={user.phone ? 'Изменить' : 'Добавить'} onAction={() => setDialog('phone')} />
        <Row
          label="Почта"
          value={user.email ?? (user.email_pending ? `${user.email_pending} — не подтверждена` : 'Не указана')}
          action={user.email || user.email_pending ? 'Изменить' : 'Добавить'}
          onAction={() => setDialog('email')}
        />
      </div>
      {user.email_pending ? (
        <p className={styles.pendingNote}>
          Письмо со ссылкой отправлено на {user.email_pending}. Не пришло — загляните в «Спам» или{' '}
          {resend === 'sent' ? (
            'отправили ещё раз'
          ) : resend === 'error' ? (
            'попробуйте позже — не чаще раза в минуту'
          ) : (
            <button type="button" className={auth.link} onClick={() => void resendEmail()} disabled={resend === 'busy'}>отправьте ещё раз</button>
          )}
          .
        </p>
      ) : null}

      <div className={layer.divider} />
      <h2 className={layer.subTitle}>Пароль</h2>
      <p className={layer.text}>После смены на других устройствах придётся войти заново.</p>
      <div className={layer.buttonRow}>
        <button type="button" className={layer.button} onClick={() => setDialog('password')}>Сменить пароль</button>
      </div>

      <div className={layer.divider} />
      <h2 className={layer.subTitle}>Двухфакторная аутентификация</h2>
      <p className={layer.text}>
        При входе понадобится код из приложения-аутентификатора: Яндекс Ключ, Google Authenticator, Aegis.
      </p>
      <div className={styles.mfaStatus}>
        {enabled ? <ShieldCheck size={20} className={styles.mfaOn} /> : <ShieldOff size={20} className={styles.mfaOff} />}
        <span>
          {enabled ? 'Двухфакторная аутентификация включена' : 'Двухфакторная аутентификация выключена'}
          {enabled && mfa.data ? <span className={styles.mfaCodes}> · резервных кодов осталось: {mfa.data.backup_codes_left}</span> : null}
        </span>
      </div>
      <div className={layer.buttonRow}>
        {enabled ? (
          <>
            <button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={() => setDialog('codes')}>Новые резервные коды</button>
            <button type="button" className={cx(layer.button, layer.buttonDanger)} onClick={() => setDialog('totp-disable')}>Отключить 2FA</button>
          </>
        ) : (
          <button type="button" className={layer.button} onClick={() => setDialog('totp-enable')}>Включить приложение-аутентификатор</button>
        )}
      </div>

      <DevicesSection />

      <div className={layer.divider} />
      <h2 className={layer.subTitle}>Мои данные</h2>
      <ul className={layer.points}>
        <li>в архиве — профиль, серверы, ваши сообщения и вложения, друзья и настройки; чужих сообщений нет</li>
        <li>готовится в фоне, ссылка действует неделю</li>
      </ul>
      <DataExportBlock />

      <div className={layer.divider} />
      <h2 className={layer.subTitle}>Удаление учётной записи</h2>
      <ul className={layer.points}>
        <li>удаление — через 14 дней после заявки, до этого его можно отменить</li>
        <li>номер и почта освобождаются</li>
        <li>отправленные сообщения останутся у собеседников без вашего имени</li>
      </ul>
      <div className={layer.buttonRow}>
        <button type="button" className={cx(layer.button, layer.buttonDanger)} onClick={() => setDialog('delete')}>
          <Trash2 size={14} /> Удалить учётную запись
        </button>
      </div>

      {dialog === 'username' ? <UsernameModal current={user.username} onClose={() => setDialog(null)} /> : null}
      {dialog === 'phone' ? <PhoneModal onClose={() => setDialog(null)} /> : null}
      {dialog === 'email' ? <EmailModal onClose={() => setDialog(null)} /> : null}
      {dialog === 'name' ? <DisplayNameModal current={user.global_name ?? ''} onClose={() => setDialog(null)} /> : null}
      {dialog === 'password' ? <PasswordModal onClose={() => setDialog(null)} /> : null}
      {dialog === 'totp-enable' ? <TotpEnableModal username={user.username} onClose={() => setDialog(null)} /> : null}
      {dialog === 'totp-disable' ? <TotpDisableModal onClose={() => setDialog(null)} /> : null}
      {dialog === 'codes' ? <BackupCodesModal onClose={() => setDialog(null)} /> : null}
      {dialog === 'delete' ? <DeleteAccountModal onClose={() => setDialog(null)} /> : null}
    </section>
  )
}

function Row({ label, value, action, onAction }: { label: string; value: string; action?: string; onAction?: () => void }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowText}>
        <span className={styles.rowLabel}>{label}</span>
        <span className={styles.rowValue}>{value}</span>
      </div>
      {action && onAction ? <button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={onAction}>{action}</button> : null}
    </div>
  )
}

function useSubmit() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string | undefined>>({})
  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    setFields({})
    try {
      await fn()
    } catch (err) {
      const d = describeError(err)
      setError(d.message)
      setFields(d.fields)
    } finally {
      setBusy(false)
    }
  }
  return { busy, error, fields, run }
}

/** Смена номера: пароль и новый номер → код из SMS → готово. */
function PhoneModal({ onClose }: { onClose: () => void }) {
  const setUser = useAuthStore((s) => s.setUser)
  const [step, setStep] = useState<'phone' | 'waiting' | 'code' | 'done'>('phone')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [start, setStart] = useState<PhoneStart | null>(null)
  const [code, setCode] = useState('')
  const { busy, error, fields, run } = useSubmit()

  const sendCode = (e?: FormEvent) => {
    e?.preventDefault()
    void run(async () => {
      const res = await authApi.changePhoneStart({ password, phone })
      setStart(res)
      setCode('')
      setStep(res.status === 'pending' ? 'waiting' : 'code')
      if (res.status === 'ok') await complete(res, '')
    })
  }
  const complete = async (s: PhoneStart, value: string) => {
    setUser(await authApi.changePhoneComplete({ verification_id: s.verification_id, code: value }))
    setStep('done')
  }
  const confirm = (e: FormEvent) => {
    e.preventDefault()
    if (!start) return
    void run(() => complete(start, code))
  }

  const phoneState = useVerifyStatus(start, 'change_phone')
  const { status, codeLength } = phoneState
  // пока оператор не ответил — ждём; ответил «нужен код» — спрашиваем как обычно
  const asking = step === 'code' || (step === 'waiting' && status !== 'pending' && status !== 'ok')

  // оператор подтвердил номер сам — завершаем без участия человека
  useEffect(() => {
    // и с экрана ввода кода тоже: туда можно уйти руками, не дождавшись уведомления
    if ((step !== 'waiting' && step !== 'code') || status !== 'ok' || !start) return
    let alive = true
    void (async () => {
      try {
        const user = await authApi.changePhoneComplete({ verification_id: start.verification_id, code: '' })
        if (alive) {
          setUser(user)
          setStep('done')
        }
      } catch {
        // подтверждение не прошло — дальше как обычно, по коду из SMS
        if (alive) setStep('code')
      }
    })()
    return () => {
      alive = false
    }
  }, [status, step, start, setUser])

  return (
    <Modal title="Изменить номер телефона" onClose={onClose}>
      {step === 'phone' ? (
        <form className={auth.form} onSubmit={sendCode} noValidate>
          <p className={layer.text}>На новый номер придёт код подтверждения. Для безопасности введите текущий пароль.</p>
          <Field label="Текущий пароль" error={fields.password} htmlFor="phone-password">
            <input id="phone-password" className={auth.input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
          </Field>
          <Field label="Новый номер телефона" error={fields.phone} htmlFor="phone-new">
            <input id="phone-new" className={auth.input} type="tel" inputMode="tel" autoComplete="tel" placeholder="+7 900 000-00-00" value={phone} onChange={(e) => setPhone(e.target.value)} required />
          </Field>
          {error && !fields.password && !fields.phone ? <p className={auth.error} role="alert">{error}</p> : null}
          <button type="submit" className={auth.primary} disabled={busy || !password || phone.replace(/\D/g, '').length < 10}>{busy ? 'Отправляем…' : 'Получить код'}</button>
        </form>
      ) : step === 'waiting' && !asking ? (
        <div className={auth.form}>
          <p className={layer.text}>
            Оператор подтверждает {start?.phone ?? phone} сам — если придёт уведомление, нажмите в нём «Подтвердить».
            Код вводить не нужно.
          </p>
          <p className={layer.text} aria-live="polite">Ждём ответа…</p>
          {error ? <p className={auth.error} role="alert">{error}</p> : null}
          <button type="button" className={auth.primary} onClick={() => setStep('code')}>Ввести код из SMS</button>
        </div>
      ) : asking ? (
        <form className={auth.form} onSubmit={confirm} noValidate>
          <p className={layer.text}>
            Отправили код на {start?.phone ?? phone}
            {start?.channel === 'console' ? ' (режим разработки: код в логе сервера)' : ''}.
            {phoneState.channel === 'mobileid' ? ' Отправителем будет ваш оператор, а не Кличат.' : ''}
          </p>
          <Field label="Код из SMS" error={fields.code} htmlFor="phone-code">
            <CodeInput id="phone-code" value={code} onChange={setCode} length={codeLength} />
          </Field>
          <CodeFallback state={phoneState} />
          {error && !fields.code ? <p className={auth.error} role="alert">{error}</p> : null}
          <button type="submit" className={auth.primary} disabled={busy || code.length !== codeLength}>{busy ? 'Проверяем…' : 'Подтвердить'}</button>
          <p className={auth.footer}>
            <button type="button" className={auth.link} onClick={() => sendCode()} disabled={busy}>Отправить код ещё раз</button>
            {' · '}
            <button type="button" className={auth.link} onClick={() => setStep('phone')}>Изменить номер</button>
          </p>
        </form>
      ) : (
        <div className={auth.form}>
          <p className={layer.text}>Номер изменён. Теперь для входа и восстановления пароля используется новый номер.</p>
          <button type="button" className={auth.primary} onClick={onClose}>Готово</button>
        </div>
      )}
    </Modal>
  )
}

/** Смена пароля: текущий и новый. Остальные сеансы сервер завершает сам, этот остаётся. */
function PasswordModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [password, setPassword] = useState('')
  const [next, setNext] = useState('')
  const [done, setDone] = useState(false)
  const { busy, error, fields, run } = useSubmit()

  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      await authApi.changePassword({ password, new_password: next })
      // список устройств ниже на этой же странице: в нём осталось только это
      void qc.invalidateQueries({ queryKey: ['sessions'] })
      setDone(true)
    })
  }

  return (
    <Modal title="Сменить пароль" onClose={onClose}>
      {done ? (
        <div className={auth.form}>
          <p className={layer.text}>Пароль изменён. На других устройствах нужно войти заново.</p>
          <button type="button" className={auth.primary} onClick={onClose}>Готово</button>
        </div>
      ) : (
        <form className={auth.form} onSubmit={submit} noValidate>
          <Field label="Текущий пароль" error={fields.password} htmlFor="password-current">
            <input id="password-current" className={auth.input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
          </Field>
          <Field label="Новый пароль" hint="Не короче 8 символов" error={fields.new_password} htmlFor="password-new">
            <input id="password-new" className={auth.input} type="password" autoComplete="new-password" maxLength={128} value={next} onChange={(e) => setNext(e.target.value)} required />
          </Field>
          {error && !fields.password && !fields.new_password ? <p className={auth.error} role="alert">{error}</p> : null}
          <button type="submit" className={auth.primary} disabled={busy || !password || next.length < 8}>{busy ? 'Сохраняем…' : 'Сменить пароль'}</button>
          <p className={auth.footer}>Не помните текущий пароль — выйдите и нажмите «Забыли пароль?» на странице входа.</p>
        </form>
      )}
    </Modal>
  )
}

/** Предупреждение о заявке на удаление: висит, пока её не отменили. */
function DeletionNotice() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (!user?.deletion_requested_at) return null
  const when = new Date(new Date(user.deletion_requested_at).getTime() + 14 * 86400000)
  const cancel = () => {
    setBusy(true)
    setError(null)
    void authApi
      .cancelDeletion()
      .then(async () => setUser(await authApi.me()))
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }
  return (
    <div className={styles.deletionNotice} role="alert">
      <span>
        Учётная запись будет удалена {formatDateTime(when)}. До этого дня всё работает как обычно — заявку можно отменить.
      </span>
      {error ? <span className={auth.error}>{error}</span> : null}
      <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={busy} onClick={cancel}>
        {busy ? 'Отменяем…' : 'Отменить удаление'}
      </button>
    </div>
  )
}

/** «МБ» из байтов — размер архива человеку. */
function sizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`
}

/** Выгрузка данных: заказ, ожидание и ссылка на архив. */
function DataExportBlock() {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['data-export'],
    queryFn: authApi.exportStatus,
    // пока собирается — переспрашиваем: готовность приходит не событием, а по факту
    refetchInterval: (query) => ((query.state.data as DataExport | undefined)?.status === 'pending' ? 5000 : false),
  })
  const request = () => {
    setBusy(true)
    setError(null)
    void authApi
      .requestExport()
      .then((res) => qc.setQueryData(['data-export'], res))
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }
  const data = q.data
  return (
    <div>
      {data?.status === 'pending' ? (
        <p className={layer.text} aria-live="polite">Собираем архив… Это займёт минуту-другую, страницу можно закрыть.</p>
      ) : data?.status === 'ready' && data.url ? (
        <p className={layer.text}>
          Архив готов{data.size_bytes ? `, ${sizeText(data.size_bytes)}` : ''}
          {data.expires_at ? ` — ссылка работает до ${formatDateTime(new Date(data.expires_at))}` : ''}.
        </p>
      ) : data?.status === 'failed' ? (
        <p className={auth.error} role="alert">Прошлая выгрузка не собралась. Попробуйте ещё раз.</p>
      ) : null}
      {error ? <p className={auth.error} role="alert">{error}</p> : null}
      <div className={layer.buttonRow}>
        {data?.status === 'ready' && data.url ? (
          <a className={layer.button} href={data.url} download>
            <Download size={14} /> Скачать архив
          </a>
        ) : null}
        <button
          type="button"
          className={cx(layer.button, data?.status === 'ready' && layer.buttonSecondary)}
          disabled={busy || data?.status === 'pending'}
          onClick={request}
        >
          {busy ? 'Заказываем…' : data?.status === 'ready' ? 'Собрать заново' : 'Запросить выгрузку'}
        </button>
      </div>
    </div>
  )
}

/** Удаление: пароль и подтверждение. Серверы с участниками надо передать до этого. */
function DeleteAccountModal({ onClose }: { onClose: () => void }) {
  const setUser = useAuthStore((s) => s.setUser)
  const [password, setPassword] = useState('')
  const [done, setDone] = useState<Date | null>(null)
  const { busy, error, fields, run } = useSubmit()
  const passwordError = fields.password ?? (error?.startsWith('Неверный номер') ? 'Неверный пароль' : undefined)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      const res = await authApi.requestDeletion(password)
      setUser(await authApi.me())
      setDone(res.delete_at ? new Date(res.delete_at) : new Date())
    })
  }

  return (
    <Modal title="Удалить учётную запись" onClose={onClose}>
      {done ? (
        <div className={auth.form}>
          <p className={layer.text}>
            Заявка принята: учётная запись будет удалена {formatDateTime(done)}. До этого дня всё работает как обычно,
            а отменить удаление можно здесь же, в настройках.
          </p>
          <button type="button" className={auth.primary} onClick={onClose}>Понятно</button>
        </div>
      ) : (
        <form className={auth.form} onSubmit={submit} noValidate>
          <ul className={layer.points}>
            <li>удаление — через 14 дней, до этого его можно отменить</li>
            <li>потом номер и почта освобождаются, а войти будет нечем</li>
            <li>сообщения останутся у собеседников без вашего имени</li>
          </ul>
          <Field label="Текущий пароль" error={passwordError} htmlFor="delete-password">
            <input id="delete-password" className={auth.input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
          </Field>
          {fields.guilds ? <p className={auth.error} role="alert">{fields.guilds}</p> : null}
          {error && !passwordError && !fields.guilds ? <p className={auth.error} role="alert">{error}</p> : null}
          <button type="submit" className={cx(auth.primary, styles.dangerPrimary)} disabled={busy || !password}>
            {busy ? 'Отправляем…' : 'Удалить через 14 дней'}
          </button>
        </form>
      )}
    </Modal>
  )
}

/** Почта: пароль и адрес → письмо со ссылкой; отвязка — тем же паролем. */
function EmailModal({ onClose }: { onClose: () => void }) {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [step, setStep] = useState<'form' | 'sent' | 'removed'>('form')
  const { busy, error, fields, run } = useSubmit()
  // сервер отвечает общим «неверный номер или пароль» — здесь номера нет, подсвечиваем пароль
  const passwordError = fields.password ?? (error?.startsWith('Неверный номер') ? 'Неверный пароль' : undefined)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      setUser(await authApi.emailStart({ password, email: email.trim() }))
      setStep('sent')
    })
  }
  const remove = () => {
    void run(async () => {
      setUser(await authApi.emailRemove(password))
      setStep('removed')
    })
  }

  return (
    <Modal title={user?.email ? 'Изменить почту' : 'Добавить почту'} onClose={onClose}>
      {step === 'form' ? (
        <form className={auth.form} onSubmit={submit} noValidate>
          <p className={layer.text}>
            На новый адрес придёт письмо со ссылкой — почта привяжется после перехода по ней.
            Для безопасности введите текущий пароль.
          </p>
          <Field label="Текущий пароль" error={passwordError} htmlFor="email-password">
            <input id="email-password" className={auth.input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
          </Field>
          <Field label="Почта" error={fields.email} htmlFor="email-new">
            <input id="email-new" className={auth.input} type="email" inputMode="email" autoComplete="email" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} required />
          </Field>
          {error && !passwordError && !fields.email ? <p className={auth.error} role="alert">{error}</p> : null}
          <button type="submit" className={auth.primary} disabled={busy || !password || !email.includes('@')}>{busy ? 'Отправляем…' : 'Отправить письмо'}</button>
          {user?.email ? (
            <p className={auth.footer}>
              {user.phone ? (
                <button type="button" className={auth.link} onClick={remove} disabled={busy || !password}>Отвязать {user.email}</button>
              ) : (
                <span className={layer.text}>Почта — единственный способ входа: отвязать её можно после того, как добавите номер.</span>
              )}
            </p>
          ) : null}
        </form>
      ) : step === 'sent' ? (
        <div className={auth.form}>
          <p className={layer.text}>
            Отправили письмо на {email.trim()}. Перейдите по ссылке из него — и почта привяжется.
            Ссылка действует сутки; не пришло — загляните в «Спам».
          </p>
          <button type="button" className={auth.primary} onClick={onClose}>Готово</button>
        </div>
      ) : (
        <div className={auth.form}>
          <p className={layer.text}>Почта отвязана. Восстановить пароль теперь можно только по номеру телефона.</p>
          <button type="button" className={auth.primary} onClick={onClose}>Готово</button>
        </div>
      )}
    </Modal>
  )
}

/** Включение TOTP: QR и секрет → пароль и код → резервные коды. */
function TotpEnableModal({ username, onClose }: { username: string; onClose: () => void }) {
  const qc = useQueryClient()
  const patchUser = useAuthStore((s) => s.patchUser)
  const [secret] = useState(generateTotpSecret)
  const [qr, setQr] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const { busy, error, fields, run } = useSubmit()

  useEffect(() => {
    let cancelled = false
    QRCode.toDataURL(otpauthUrl(secret, username), { width: 176, margin: 1, color: { dark: '#000000', light: '#ffffff' } })
      .then((url) => { if (!cancelled) setQr(url) })
      .catch(() => { if (!cancelled) setQr(null) })
    return () => { cancelled = true }
  }, [secret, username])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      const res = await authApi.enableTOTP({ password, secret, code })
      setCodes(res.backup_codes)
      patchUser({ mfa_enabled: true })
      void qc.invalidateQueries({ queryKey: ['mfa'] })
    })
  }

  if (codes) {
    return (
      <Modal title="Резервные коды" onClose={onClose}>
        <BackupCodes codes={codes} />
        <button type="button" className={auth.primary} onClick={onClose}>Готово</button>
      </Modal>
    )
  }

  return (
    <Modal title="Включить двухфакторную аутентификацию" onClose={onClose} width={520}>
      <form className={auth.form} onSubmit={submit} noValidate>
        <div className={styles.qrRow}>
          <div className={styles.qrBox}>
            {qr ? <img src={qr} alt="QR-код для приложения-аутентификатора" width={176} height={176} /> : <span className={styles.qrLoading}>QR…</span>}
          </div>
          <div className={styles.qrText}>
            <p className={layer.text}>Отсканируйте QR-код в приложении-аутентификаторе или введите ключ вручную:</p>
            <code className={styles.secret}>{formatSecret(secret)}</code>
            <p className={cx(layer.text, styles.secretHint)}>Алгоритм SHA1, 6 цифр, 30 секунд.</p>
          </div>
        </div>
        <Field label="Пароль" error={fields.password} htmlFor="totp-password">
          <input id="totp-password" className={auth.input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Field label="Код из приложения" error={fields.code} htmlFor="totp-code">
          <CodeInput id="totp-code" value={code} onChange={setCode} />
        </Field>
        {error && !fields.password && !fields.code ? <p className={auth.error} role="alert">{error}</p> : null}
        <button type="submit" className={auth.primary} disabled={busy || !password || code.length !== 6}>{busy ? 'Включаем…' : 'Включить'}</button>
      </form>
    </Modal>
  )
}

function TotpDisableModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const patchUser = useAuthStore((s) => s.patchUser)
  const [code, setCode] = useState('')
  const { busy, error, fields, run } = useSubmit()
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      await authApi.disableTOTP(code.trim())
      patchUser({ mfa_enabled: false })
      void qc.invalidateQueries({ queryKey: ['mfa'] })
      onClose()
    })
  }
  return (
    <Modal title="Отключить двухфакторную аутентификацию" onClose={onClose}>
      <form className={auth.form} onSubmit={submit} noValidate>
        <p className={layer.text}>Введите код из приложения-аутентификатора или резервный код. После отключения для входа хватит пароля.</p>
        <Field label="Код" error={fields.code} htmlFor="totp-off-code">
          <input id="totp-off-code" className={auth.input} autoComplete="one-time-code" spellCheck={false} placeholder="123456 или xxxxxxxx-xxxx" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        </Field>
        {error && !fields.code ? <p className={auth.error} role="alert">{error}</p> : null}
        <button type="submit" className={cx(auth.primary, styles.dangerPrimary)} disabled={busy || code.trim().length < 6}>{busy ? 'Отключаем…' : 'Отключить'}</button>
      </form>
    </Modal>
  )
}

function BackupCodesModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [password, setPassword] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const { busy, error, fields, run } = useSubmit()
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      setCodes((await authApi.regenerateBackupCodes(password)).backup_codes)
      void qc.invalidateQueries({ queryKey: ['mfa'] })
    })
  }
  return (
    <Modal title="Резервные коды" onClose={onClose}>
      {codes ? (
        <>
          <BackupCodes codes={codes} />
          <button type="button" className={auth.primary} onClick={onClose}>Готово</button>
        </>
      ) : (
        <form className={auth.form} onSubmit={submit} noValidate>
          <p className={layer.text}>Старые резервные коды перестанут действовать. Для подтверждения введите пароль.</p>
          <Field label="Пароль" error={fields.password} htmlFor="codes-password">
            <input id="codes-password" className={auth.input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
          </Field>
          {error && !fields.password ? <p className={auth.error} role="alert">{error}</p> : null}
          <button type="submit" className={auth.primary} disabled={busy || !password}>{busy ? 'Создаём…' : 'Создать новые коды'}</button>
        </form>
      )}
    </Modal>
  )
}

function BackupCodes({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* буфер обмена недоступен — коды видны на экране */
    }
  }
  return (
    <>
      <p className={layer.text}>Сохраните коды: каждый работает один раз и заменяет код из приложения.</p>
      <ul className={styles.codes} aria-label="Резервные коды">
        {codes.map((c) => <li key={c}><code>{c}</code></li>)}
      </ul>
      <button type="button" className={cx(layer.button, layer.buttonSecondary, styles.copy)} onClick={() => void copy()}>
        <Copy size={16} /> {copied ? 'Скопировано' : 'Скопировать'}
      </button>
    </>
  )
}

export function DisplayNameModal({ current, onClose }: { current: string; onClose: () => void }) {
  const setUser = useAuthStore((s) => s.setUser)
  const [value, setValue] = useState(current)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      setUser(await authApi.updateProfile({ display_name: value.trim() }))
      onClose()
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal title="Отображаемое имя" onClose={onClose}>
      <form className={auth.form} onSubmit={submit} noValidate>
        <Field label="Имя" htmlFor="display-name" error={undefined}>
          <input id="display-name" className={auth.input} maxLength={32} placeholder="Как вас показывать" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
        </Field>
        <p className={auth.subtitle}>Пустое имя — будет показываться ник. До 32 символов.</p>
        {error ? <p className={auth.error} role="alert">{error}</p> : null}
        <button type="submit" className={auth.primary} disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
      </form>
    </Modal>
  )
}
